import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, ArrowLeft, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { verifyResetToken, resetPassword } from '../lib/api';

const ResetPasswordPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [loading, setLoading] = useState(true);
    const [validating, setValidating] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setError('No reset token provided');
                setValidating(false);
                setLoading(false);
                return;
            }

            try {
                const res = await verifyResetToken(token);
                setTokenValid(res.data.valid);
                setEmail(res.data.email);
            } catch (err) {
                setError(err.response?.data?.detail || 'Invalid or expired reset link');
                setTokenValid(false);
            } finally {
                setValidating(false);
                setLoading(false);
            }
        };

        validateToken();
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setLoading(true);

        try {
            await resetPassword(token, password);
            setSuccess(true);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (validating) {
        return (
            <Layout>
                <div className="min-h-[70vh] bg-gray-50 py-12 flex items-center justify-center">
                    <div className="text-center">
                        <div className="spinner mx-auto mb-4" />
                        <p className="text-gray-600">Validating reset link...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="min-h-[70vh] bg-gray-50 py-12">
                <div className="max-w-md mx-auto px-4">
                    <Link to="/advertiser/login" className="inline-flex items-center text-gray-600 hover:text-navy mb-6">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Login
                    </Link>

                    <Card>
                        <CardHeader className="text-center">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${
                                success ? 'bg-green-100' : tokenValid ? 'bg-navy/10' : 'bg-red-100'
                            }`}>
                                {success ? (
                                    <CheckCircle className="w-6 h-6 text-green-600" />
                                ) : tokenValid ? (
                                    <Lock className="w-6 h-6 text-navy" />
                                ) : (
                                    <AlertCircle className="w-6 h-6 text-red-600" />
                                )}
                            </div>
                            <CardTitle className="text-2xl">
                                {success ? 'Password Reset!' : tokenValid ? 'Reset Your Password' : 'Invalid Link'}
                            </CardTitle>
                            <CardDescription>
                                {success 
                                    ? 'Your password has been successfully reset.'
                                    : tokenValid 
                                        ? `Enter a new password for ${email}`
                                        : 'This password reset link is invalid or has expired.'
                                }
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {success ? (
                                <div className="space-y-4">
                                    <Alert className="bg-green-50 border-green-200">
                                        <CheckCircle className="h-4 w-4 text-green-600" />
                                        <AlertDescription className="text-green-800">
                                            You can now log in with your new password.
                                        </AlertDescription>
                                    </Alert>

                                    <Button 
                                        onClick={() => navigate('/advertiser/login')}
                                        className="w-full bg-navy hover:bg-navy/90"
                                        data-testid="go-to-login"
                                    >
                                        Go to Login
                                    </Button>
                                </div>
                            ) : tokenValid ? (
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    {error && (
                                        <Alert variant="destructive">
                                            <AlertCircle className="h-4 w-4" />
                                            <AlertDescription>{error}</AlertDescription>
                                        </Alert>
                                    )}

                                    <div className="space-y-2">
                                        <Label htmlFor="password">New Password</Label>
                                        <div className="relative">
                                            <Input
                                                id="password"
                                                type={showPassword ? 'text' : 'password'}
                                                placeholder="Enter new password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                required
                                                minLength={6}
                                                data-testid="new-password-input"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                            >
                                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                        <p className="text-xs text-gray-500">Must be at least 6 characters</p>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="confirmPassword">Confirm New Password</Label>
                                        <Input
                                            id="confirmPassword"
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="Confirm new password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            required
                                            data-testid="confirm-password-input"
                                        />
                                    </div>

                                    <Button 
                                        type="submit" 
                                        className="w-full bg-navy hover:bg-navy/90"
                                        disabled={loading}
                                        data-testid="reset-password-submit"
                                    >
                                        {loading ? 'Resetting...' : 'Reset Password'}
                                    </Button>
                                </form>
                            ) : (
                                <div className="space-y-4">
                                    <Alert variant="destructive">
                                        <AlertCircle className="h-4 w-4" />
                                        <AlertDescription>{error}</AlertDescription>
                                    </Alert>

                                    <Link to="/forgot-password">
                                        <Button variant="outline" className="w-full">
                                            Request New Reset Link
                                        </Button>
                                    </Link>

                                    <p className="text-center text-sm text-gray-500">
                                        Remember your password?{' '}
                                        <Link to="/advertiser/login" className="text-navy hover:underline">
                                            Sign in
                                        </Link>
                                    </p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </Layout>
    );
};

export default ResetPasswordPage;
